/**
 * 历史记录处理工具函数
 */
import { OpType, VideoType, TVCoverData } from '@/types/common';
import {
  PosterLabelType,
  markLabelPositionToKey,
  HistoryResItem,
  HistorySaveItem,
  HistoryModule,
  HistoryModuleType,
  LocalHistoryInfo,
  HistoryUpdateParams,
  HistoryUpdateCurShowParams,
  HistoryUiData,
  HistoryShowItem,
} from '@/types/history';
import { localStorageKey } from '@/utils/constants';
import { HistoryPageText } from '@/pages/history/constants/history';

const { today: todayText, week: weekText, early: earlyText } = HistoryPageText;

export const maxHistoryCount = 300;

export const handleCoverAndUiData = (uiData: HistoryUiData[], coverData: TVCoverData[]): HistoryShowItem[] => {
  const historyMap = new Map<string, HistoryShowItem>();
  uiData.forEach((uiItem) => {
    const {
      cid,
      vid,
      pid,
      video_play_time: videoPlayTime,
      video_total_time: videoTotalTime,
      main_title: mainTitle,
      img_url: imgUrl = '',
      modify_time: modifyTime,
      type,
    } = uiItem;

    let progress = videoPlayTime && videoTotalTime ? Math.floor((videoPlayTime / videoTotalTime) * 100) : 0;
    progress = progress > 100 ? 100 : progress;

    historyMap.set(cid, {
      cid,
      vid,
      pid,
      vidTitle: mainTitle || '',
      vidCover: {
        horizontal: imgUrl,
        vertical: '',
      },
      modifyTime,
      progress,
      videoPlayTime,
      videoTotalTime,
      type,
    });
  });

  coverData.forEach((coverItem) => {
    const {
      cover_id: cid,
      title: coverTitle,
      episode_updated: episodeUpdated,
      ott_tags: ottTags = [],
      art_word: artTitle,
      pic_url_vt: picUrlVt = '',
      pure_pic_url_vt: purePicUrlVt = '',
      pic_url_hz: picUrlHz = '',
      pure_pic_url_hz: purePicUrlHz = '',
    } = coverItem;

    const cornerLabels = ottTags.map((tag) => {
      const { text, position, url } = tag;
      const type = url ? PosterLabelType.IMG : PosterLabelType.TEXT;
      const content = url || text;
      const positionKey = markLabelPositionToKey[position];
      return {
        position: positionKey,
        content,
        type,
      };
    });
    if (historyMap.has(cid)) {
      const historyItem = historyMap.get(cid);
      historyMap.set(cid, {
        ...historyItem,
        title: coverTitle,
        artTitle,
        updateInfo: episodeUpdated,
        fallbackCover: {
          horizontal: purePicUrlHz || '',
          vertical: purePicUrlVt || '',
        },
        cover: {
          horizontal: picUrlHz || '',
          vertical: picUrlVt || '',
        },
        cornerLabels,
      });
    }
  });

  // 返回转换后的数组，需要补全 HistoryShowItem 的其他字段
  return Array.from(historyMap.values())
    .map((item) => ({
      title: '', // 需要根据实际需求设置
      artTitle: '', // 需要根据实际需求设置
      ...item,
    }))
    .sort((a, b) => {
      const timeA = parseInt(a.modifyTime || '0', 10);
      const timeB = parseInt(b.modifyTime || '0', 10);
      return timeB - timeA;
    });
};

// historyUiData过滤掉直播类型以及短视频，以及vid为空的坏数据 以及 未下架的
export const filterHistory = (historyList: HistoryUiData[]): HistoryUiData[] => {
  return historyList.filter(
    (item) =>
      item.type !== VideoType.LIVE &&
      item.type !== VideoType.SHORT &&
      item.vid &&
      item.vid === item.vid &&
      !item.off_shelf_status
  );
};

// 处理历史记录数据，将线上历史记录和本地历史记录合并
export const handleHistoryData = (
  initialHistory: HistorySaveItem[],
  historyRes: HistoryResItem[]
): HistorySaveItem[] => {
  const historyMap = new Map<string, HistorySaveItem>();

  // 去重
  initialHistory.forEach((item) => {
    const key = item.cid;
    historyMap.set(key, item);
  });

  // 线上历史记录正序排列(时间从小到大)
  const tmpHistoryRes = historyRes.sort((a, b) => {
    const timeA = parseInt(a.record.modify_time, 10);
    const timeB = parseInt(b.record.modify_time, 10);
    return timeA - timeB;
  });

  tmpHistoryRes.forEach((item) => {
    const { record, operation_type: operationType } = item;
    const key = record.cid;

    if (operationType === OpType.OpTypeDel) {
      historyMap.delete(key);
      return;
    }

    historyMap.set(key, {
      vid: record.vid,
      cid: record.cid,
      pid: record.pid || '',
      modifyTime: record.modify_time,
      videoPlayTime: record.video_play_time,
      videoTotalTime: record.video_total_time,
      type: record.type,
    });
  });

  // 转换为数组并按时间降序排序，最新的在前，方便展示，过滤坏数据
  return (
    Array.from(historyMap.values())
      .sort((a, b) => {
        const timeA = parseInt(a.modifyTime, 10);
        const timeB = parseInt(b.modifyTime, 10);
        return timeB - timeA;
      })
      ?.filter(
        (item) => item.type !== VideoType.LIVE && item.type !== VideoType.SHORT && item.vid && item.vid === item.vid
      ) || []
  ).slice(0, maxHistoryCount);
};

// 将历史记录分组
export const historyToGroup = (historyList: HistoryShowItem[]): HistoryModule[] => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayStartTime = todayStart.getTime();
  const weekAgoTime = todayStartTime - 7 * 24 * 60 * 60 * 1000;

  // 分类存储
  const today: HistoryShowItem[] = [];
  const week: HistoryShowItem[] = [];
  const earlier: HistoryShowItem[] = [];

  historyList.forEach((item) => {
    const modifyTime = parseInt(item.modifyTime || '0', 10);
    if (modifyTime >= todayStartTime) {
      // 当天
      today.push({
        ...item,
        idx: today.length,
      });
    } else if (modifyTime >= weekAgoTime) {
      // 一周内
      week.push({
        ...item,
        idx: week.length,
      });
    } else {
      // 更早
      earlier.push({
        ...item,
        idx: earlier.length,
      });
    }
  });

  // 构建返回结果
  const modules: HistoryModule[] = [];

  let moduleIdx = 0;
  if (today.length > 0) {
    moduleIdx = moduleIdx + 1;
  }
  modules.push({
    moduleTitle: todayText,
    moduleIdx,
    moduleType: HistoryModuleType.TodayModule,
    historyList: today,
  });

  if (week.length > 0) {
    moduleIdx = moduleIdx + 1;
  }
  modules.push({
    moduleTitle: weekText,
    moduleIdx,
    moduleType: HistoryModuleType.WeekModule,
    historyList: week,
  });

  if (week.length > 0) {
    moduleIdx = moduleIdx + 1;
  }
  modules.push({
    moduleTitle: earlyText,
    moduleIdx,
    moduleType: HistoryModuleType.EarlyModule,
    historyList: earlier,
  });

  return modules;
};

// 获取本地存储key
export function getStorageKey(isLogin: boolean): string {
  return isLogin ? localStorageKey.linux_tv_history_user_login : localStorageKey.linux_tv_history_user_no_login;
}

// 保存历史记录到本地
export function saveHistoryToStorage({ history, dataVersion, vuid }: LocalHistoryInfo): void {
  try {
    const isLogin = !!vuid;
    const storageKey = getStorageKey(isLogin);
    const data: LocalHistoryInfo = { history: history || [] };
    data.history = data.history.slice(0, maxHistoryCount);
    if (!!isLogin) {
      data.vuid = vuid;
      const storedData = localStorage.getItem(storageKey);
      if (!!storedData) {
        let storedParsed: any;
        try {
          storedParsed = JSON.parse(storedData);
        } catch (parseError) {
          console.warn(`Failed to parse existing localStorage data for key "${storageKey}":`, parseError);
          // 继续执行，直接使用新数据覆盖
          storedParsed = {};
        }

        const { dataVersion: storedDataVersion, vuid: storedVuid } = storedParsed || {};
        if (vuid === storedVuid) {
          // 储存的与当前登录账号一致，dataVersion当前没传，使用本地保存的
          if (dataVersion || storedDataVersion) data.dataVersion = dataVersion || storedDataVersion;
        } else {
          // 储存的与当前登录账号不一致，使用当前登录账号传入的，没有不保存
          if (dataVersion) data.dataVersion = dataVersion;
        }
      }
    }
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save history to localStorage:', error);
    // 如果是 QuotaExceededError，尝试清空缓存
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, attempting cleanup');
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('cache') || key.includes('temp') || key.includes('aegis'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
        console.log(`Cleaned ${keysToRemove.length} cache entries`);

        // 重试保存
        try {
          localStorage.setItem(getStorageKey(!!vuid), JSON.stringify({ history: history || [], vuid }));
          console.log('Successfully saved history after cleanup');
        } catch (retryError) {
          console.error('Still failed to save after cleanup:', retryError);
        }
      } catch (cleanupError) {
        console.error('Failed to cleanup localStorage:', cleanupError);
      }
    }
  }
}

// 清空本地历史记录，区分登录以及未登录
export function deleteAllHistoryRecords(isLogin: boolean): void {
  // 清空 localStorage
  const storageKey = getStorageKey(isLogin);
  localStorage.removeItem(storageKey);
}

// 获取本地历史记录[是否登录，获取登录或者未登录的历史记录]
export function getLocalHistory(curVuid: string): LocalHistoryInfo | null {
  try {
    const isLogin = !!curVuid;
    const storageKey = getStorageKey(isLogin);
    const storedData = localStorage.getItem(storageKey);
    if (!!storedData) {
      let parsedData: any;
      try {
        parsedData = JSON.parse(storedData);
      } catch (parseError) {
        // JSON 解析失败，清除该键值对
        console.warn(`Failed to parse localStorage key "${storageKey}", clearing it:`, parseError);
        localStorage.removeItem(storageKey);
        return null;
      }

      const { history, dataVersion, vuid } = parsedData;
      if (!!isLogin && vuid !== curVuid) {
        // 如果获取的vuid和当前 localStorage 中登录的不一致 清空history，并返回null
        deleteAllHistoryRecords(isLogin);
        return null;
      }
      // 登录并且vuid一致 或者 游客状态，返回history
      if (history && Array.isArray(history)) {
        return {
          history,
          dataVersion,
          vuid: vuid || '',
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Failed to load history data from localStorage:', error);
    // 如果是 QuotaExceededError，尝试清空 localStorage
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, attempting to clear cache');
      try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && (key.includes('cache') || key.includes('temp'))) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
      } catch {
        // 清空失败，忽略
      }
    }
    return null;
  }
}

// 更新单个历史记录，包括修改删除
export function updateSingleHistory({
  currentHistory,
  historyItem,
  operationType,
  vuid,
}: HistoryUpdateParams): HistorySaveItem[] {
  // 1. 根据操作类型更新历史记录
  let updatedHistory: HistorySaveItem[] = [];

  // 删除操作：从列表中移除
  updatedHistory = currentHistory?.filter((history) => history.cid !== historyItem.cid);
  // 如果操作不是删除操作，添加到列表中第一位
  if (operationType !== OpType.OpTypeDel) {
    updatedHistory = [historyItem, ...updatedHistory];
  }

  // 保存到 localStorage
  saveHistoryToStorage({ history: updatedHistory, vuid });
  // 返回更新后的历史记录和是否需要上报到服务器
  return updatedHistory;
}

// 更新单个历史记录，包括修改删除
export function updateSingleHistoryForCurShow({
  currentHistory,
  historyItem,
  operationType,
}: HistoryUpdateCurShowParams): HistoryShowItem[] {
  // 1. 根据操作类型更新历史记录
  let updatedHistory: HistoryShowItem[] = [];

  // 删除操作：从列表中移除
  updatedHistory = currentHistory?.filter((history) => history.cid !== historyItem.cid);
  // 如果操作不是删除操作，添加到列表中第一位
  if (operationType !== OpType.OpTypeDel) {
    updatedHistory = [historyItem, ...updatedHistory];
  }

  // 返回更新后的历史记录和是否需要上报到服务器
  return updatedHistory;
}

// 查询历史记录
export function queryHistoryByCid(currentHistory: HistorySaveItem[], cid: string): HistorySaveItem | null {
  return currentHistory.find((item) => item.cid === cid) || null;
}

export const getIsSameUser = (curVuid: string, requestVuid: string) => {
  // 当前有vuid，并且vuid不等于请求时候的vuid
  return curVuid && curVuid === requestVuid;
};

// 分批处理数据的公共方法
export const processBatchData = async <T, R>(
  dataList: T[],
  batchSize: number,
  processor: (batch: T[]) => Promise<R[]>
): Promise<R[]> => {
  if (!dataList || dataList.length === 0) {
    return [];
  }

  const batches: T[][] = [];
  for (let i = 0; i < dataList.length; i += batchSize) {
    batches.push(dataList.slice(i, i + batchSize));
  }

  try {
    // 使用 Promise.all 并行处理所有批次，任何一个失败都会立即停止
    const batchResults = await Promise.all(batches.map((batch) => processor(batch)));
    return batchResults.flat();
  } catch (error) {
    console.error('Batch processing failed:', error);
    throw error;
  }
};
